import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { clockIn, clockOut, checkClockStatus } from '@/services/pos-api';
const TIMEZONE = 'America/Halifax';
function formatTime(iso) {
    return new Date(iso).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: TIMEZONE,
    });
}
function formatDuration(startIso, endIso) {
    const mins = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
}
export default function ClockModal({ isOpen, onClose }) {
    const [phase, setPhase] = useState('pin');
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusData, setStatusData] = useState(null);
    const [resultData, setResultData] = useState(null);
    const reset = useCallback(() => {
        setPhase('pin');
        setPin('');
        setError('');
        setLoading(false);
        setStatusData(null);
        setResultData(null);
    }, []);
    const handleClose = () => {
        reset();
        onClose();
    };
    const handlePinSubmit = async () => {
        if (pin.length < 4) {
            setError('PIN must be at least 4 digits');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const status = await checkClockStatus(pin);
            setStatusData({
                employeeName: status.employeeName,
                isClockedIn: status.isClockedIn,
                clockIn: status.clockIn,
            });
            setPhase('status');
        }
        catch (err) {
            setError(err.message || 'Invalid PIN');
        }
        finally {
            setLoading(false);
        }
    };
    const handleClockIn = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await clockIn(pin);
            setResultData({
                type: 'in',
                employeeName: result.employeeName,
                clockIn: result.clockIn,
            });
            setPhase('result');
            // Auto-close after 3 seconds
            setTimeout(handleClose, 3000);
        }
        catch (err) {
            setError(err.message || 'Failed to clock in');
        }
        finally {
            setLoading(false);
        }
    };
    const handleClockOut = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await clockOut(pin);
            setResultData({
                type: 'out',
                employeeName: result.employeeName,
                clockIn: result.clockIn,
                clockOut: result.clockOut,
            });
            setPhase('result');
            setTimeout(handleClose, 3000);
        }
        catch (err) {
            setError(err.message || 'Failed to clock out');
        }
        finally {
            setLoading(false);
        }
    };
    const handleKeyPress = (digit) => {
        if (pin.length < 6)
            setPin(prev => prev + digit);
    };
    if (!isOpen)
        return null;
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/60", children: _jsxs("div", { className: "bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-sm shadow-2xl", children: [phase === 'pin' && (_jsxs(_Fragment, { children: [_jsx("h2", { className: "text-lg font-bold text-white text-center mb-4", children: "Enter Your PIN" }), _jsx("input", { type: "password", inputMode: "numeric", pattern: "\\d*", autoFocus: true, value: pin, onChange: (e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setPin(val);
                            }, onKeyDown: (e) => {
                                if (e.key === 'Enter' && pin.length >= 4)
                                    handlePinSubmit();
                                if (e.key === 'Escape')
                                    handleClose();
                            }, className: "sr-only", "aria-label": "PIN input" }), _jsx("div", { className: "flex justify-center gap-2 mb-4", children: [0, 1, 2, 3].map(i => (_jsx("div", { className: `w-3 h-3 rounded-full ${i < pin.length ? 'bg-blue-400' : 'bg-slate-600'}` }, i))) }), error && (_jsx("p", { className: "text-red-400 text-sm text-center mb-3", children: error })), _jsx("div", { className: "grid grid-cols-3 gap-2 mb-4", children: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map(key => (_jsx("button", { disabled: !key || loading, onClick: () => {
                                    if (key === '⌫')
                                        setPin(prev => prev.slice(0, -1));
                                    else if (key)
                                        handleKeyPress(key);
                                }, className: `h-14 rounded-lg text-xl font-semibold transition-colors ${!key
                                    ? 'invisible'
                                    : key === '⌫'
                                        ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                        : 'bg-slate-700 text-white hover:bg-slate-600 active:bg-slate-500'}`, children: key }, key || 'empty'))) }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", onClick: handleClose, className: "flex-1 border-slate-500 text-slate-200 hover:bg-slate-700", children: "Cancel" }), _jsx(Button, { onClick: handlePinSubmit, disabled: pin.length < 4 || loading, className: "flex-1 bg-blue-600 hover:bg-blue-500", children: loading ? '...' : 'Submit' })] })] })), phase === 'status' && statusData && (_jsxs(_Fragment, { children: [_jsx("h2", { className: "text-lg font-bold text-white text-center mb-1", children: statusData.employeeName }), _jsx("p", { className: "text-slate-400 text-sm text-center mb-6", children: statusData.isClockedIn
                                ? `Clocked in since ${formatTime(statusData.clockIn)}`
                                : 'Not currently clocked in' }), error && (_jsx("p", { className: "text-red-400 text-sm text-center mb-3", children: error })), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", onClick: reset, className: "flex-1 border-slate-500 text-slate-200 hover:bg-slate-700", children: "Back" }), statusData.isClockedIn ? (_jsx(Button, { onClick: handleClockOut, disabled: loading, className: "flex-1 bg-orange-600 hover:bg-orange-500", children: loading ? '...' : 'Clock Out' })) : (_jsx(Button, { onClick: handleClockIn, disabled: loading, className: "flex-1 bg-emerald-600 hover:bg-emerald-500", children: loading ? '...' : 'Clock In' }))] })] })), phase === 'result' && resultData && (_jsxs("div", { className: "text-center", children: [_jsx("p", { className: "text-4xl mb-3", children: resultData.type === 'in' ? '✅' : '👋' }), _jsx("h2", { className: "text-lg font-bold text-white mb-1", children: resultData.employeeName }), _jsx("p", { className: "text-emerald-400 font-medium mb-1", children: resultData.type === 'in' ? 'Clocked In' : 'Clocked Out' }), resultData.type === 'in' && (_jsxs("p", { className: "text-slate-400 text-sm", children: ["at ", formatTime(resultData.clockIn)] })), resultData.type === 'out' && resultData.clockOut && (_jsxs("p", { className: "text-slate-400 text-sm", children: [formatTime(resultData.clockIn), " \u2192 ", formatTime(resultData.clockOut), ' · ', formatDuration(resultData.clockIn, resultData.clockOut)] })), _jsx("p", { className: "text-slate-500 text-xs mt-4", children: "Closing automatically..." })] }))] }) }));
}
